import classnames from 'classnames';
import { useCallback, type FunctionComponent } from 'react';

import { useDeliveryFilterConfigs } from '@//productDiscovery/Listings/components/Filters/hooks/useDeliveryFilterConfigs';
import { useFilterCount } from '@//productDiscovery/Listings/components/Filters/hooks/useFilterCount';
import { isUkPlatform } from '@/core/settings/utils';
import { ManoExpressLargeLogo } from '@/core/tamagoshi/Logos/ManoExpressLargeLogo/ManoExpressLargeLogo';
import { Gtm } from '@/core/tracking/utils/Gtm';
import { FilterBlock } from '@/productDiscovery/Listings/components/Filters/components/FilterBlock/FilterBlock';
import { useFilterConfigs } from '@/productDiscovery/Listings/hooks/useFiltersConfig';
import { type FilterConfig } from '@/productDiscovery/Listings/interfaces/filterConfig';
import { useCategoryFamilyId } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';

import { DeliveryLineFilter } from './components/DeliveryLineFilter/DeliveryLineFilter';
import {
  deliveryFilterTitle,
  freeDeliveryRevampLabel,
  manoExpressFilterName,
  relayDeliveryRevampLabel,
} from './translations';

import styles from './DeliveryFilter.module.scss';

type DeliveryAttributeName =
  | 'has_relay_delivery'
  | 'has_free_delivery'
  | 'is_mmf';

const TRACKING_MAP: Record<
  DeliveryAttributeName,
  { id: string; name: string }
> = {
  has_free_delivery: { id: 'free_delivery', name: 'Livraison' },
  has_relay_delivery: {
    id: 'relay_delivery',
    name: 'Livraison en point relais',
  },
  is_mmf: { id: 'mmf', name: 'Livraison gratuite par ManoMano' },
};

interface DeliveryFilterProps {
  className?: string;
  position: number;
}

export const DeliveryFilter: FunctionComponent<DeliveryFilterProps> = ({
  className,
  position,
}) => {
  const filterConfigs = useFilterConfigs();
  const filterCount = useFilterCount(filterConfigs);
  const deliveryFilterConfigs = useDeliveryFilterConfigs(filterConfigs);
  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();

  const pushTracking = useCallback(
    async (trackingData: {
      label: string;
      filterId: string;
      filterName: string;
      filterValue: string | boolean;
      position: number;
    }) => {
      const { generateFiltersTracking } = await import(
        '@/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking'
      );
      Gtm.push(
        generateFiltersTracking({
          e_label: trackingData.label,
          filter_id: trackingData.filterId,
          filter_name: trackingData.filterName,
          filter_value: trackingData.filterValue,
          filter_position: position,
          filter_count: filterCount,
          category_id: categoryId,
          category_level: categoryLevel,
          family_id: categoryFamilyId,
        }),
      );
    },
    [categoryFamilyId, categoryId, categoryLevel, filterCount, position],
  );

  const handleDeliveryFilterClick = useCallback(
    (attributeName: DeliveryAttributeName, newValue: boolean | string) => {
      const filterConfig = deliveryFilterConfigs.find(
        (fc) => fc.attributeName === attributeName,
      );
      if (filterConfig) {
        const trackingInfo = TRACKING_MAP[attributeName];
        pushTracking({
          label: trackingInfo.name,
          filterId: trackingInfo.id,
          filterName: trackingInfo.name,
          filterValue: newValue,
          position,
        });
      }
    },
    [deliveryFilterConfigs, pushTracking, position],
  );

  const { has_relay_delivery, has_free_delivery, is_mmf } =
    deliveryFilterConfigs.reduce<
      Partial<
        Record<DeliveryAttributeName, FilterConfig<DeliveryAttributeName>>
      >
    >((accumulator, fc) => ({ ...accumulator, [fc.attributeName]: fc }), {});

  return (
    <FilterBlock
      className={classnames(styles.root, className)}
      title={deliveryFilterTitle}
      id="delivery"
    >
      {!!is_mmf && !isUkPlatform() && (
        <DeliveryLineFilter
          className={classnames(styles.deliveryFilter, styles.manoExpress)}
          attributeName={is_mmf.attributeName}
          label={manoExpressFilterName}
          icon={<ManoExpressLargeLogo className="ml-xs" />}
          onClick={handleDeliveryFilterClick}
          manoExpress
          explanationText
        />
      )}

      {has_free_delivery && (
        <DeliveryLineFilter
          className={styles.deliveryFilter}
          attributeName={has_free_delivery.attributeName}
          label={freeDeliveryRevampLabel}
          onClick={handleDeliveryFilterClick}
        />
      )}

      {!!has_relay_delivery && (
        <DeliveryLineFilter
          className={styles.deliveryFilter}
          attributeName={has_relay_delivery.attributeName}
          label={relayDeliveryRevampLabel}
          onClick={handleDeliveryFilterClick}
        />
      )}
    </FilterBlock>
  );
};

DeliveryFilter.displayName = 'DeliveryFilter';
